import type { MeResponse } from '@exam-prep/contracts';
import { claimForCurrentClick } from '@exam-prep/ui';

import { env } from './env';

const BASE = env.VITE_API_BASE_URL;

let csrfToken = '';
const setCsrfToken = (token: string): void => {
  csrfToken = token;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> | undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get issues(): { path: string; message: string }[] {
    const d = this.details;
    const list = (d?.errors ?? d?.issues) as { path: string; message: string }[] | undefined;
    return Array.isArray(list) ? list : [];
  }
}

/**
 * Called when a signed-in request comes back 401 (the session expired or was revoked), so the app
 * can drop to the sign-in screen instead of showing errors everywhere. The sign-in endpoints
 * themselves are excluded: a wrong password or code is a normal 401 there.
 */
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null): void => {
  onUnauthorized = fn;
};
export const isAuthPath = (path: string): boolean => path.startsWith('/admin/auth/');

/** Parse a body that should be JSON; an HTML/plain error page from a proxy gives undefined. */
export function parseJson(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Build the `ApiError` for a failed response, tolerating bodies that are not our JSON shape. */
export function toApiError(path: string, status: number, json: unknown): ApiError {
  const err = (json as ErrorBody | undefined)?.error;
  if (status === 401 && !isAuthPath(path)) onUnauthorized?.();
  const fallback =
    status >= 500
      ? `The server had a problem (HTTP ${String(status)}). Try again in a moment.`
      : `HTTP ${String(status)}`;
  return new ApiError(status, err?.code ?? 'http_error', err?.message ?? fallback, err?.details);
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
}

/** A request started from a Button click keeps that button disabled with a spinner until it settles. */
function claimed<T>(request: Promise<T>): Promise<T> {
  claimForCurrentClick(request);
  return request;
}

export function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  return claimed(send<T>(method, path, body));
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = parseJson(text);
  if (!res.ok) throw toApiError(path, res.status, json);
  const me = json as Partial<MeResponse> | undefined;
  if (me && typeof me.csrfToken === 'string') setCsrfToken(me.csrfToken);
  return json as T;
}

/** Multipart upload (file + fields). The browser sets the multipart boundary. */
export function upload<T>(path: string, form: FormData): Promise<T> {
  return claimed(sendForm<T>(path, form));
}

async function sendForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { accept: 'application/json', ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}) },
    credentials: 'include',
    body: form,
  });
  const text = await res.text();
  const json = parseJson(text);
  if (!res.ok) throw toApiError(path, res.status, json);
  return json as T;
}

/** GET a non-JSON body (e.g. a CSV export) as text. Errors come back as `ApiError` like everything else. */
export async function getText(path: string): Promise<string> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  const text = await res.text();
  if (!res.ok) throw toApiError(path, res.status, parseJson(text));
  return text;
}

/** Absolute URL for API-relative paths (e.g. the streamed source file). */
export const apiUrl = (path: string): string =>
  path.startsWith('/api/') ? `${BASE}${path.slice(4)}` : path;

/**
 * Same-origin path of a KB source's streamed file, for framing it in the viewer. The admin CSP
 * only allows `frame-src 'self'` (infra/vps/caddy/Caddyfile), so this never uses a signed storage
 * URL or VITE_API_BASE_URL: Caddy (prod) and Vite (dev) proxy /api on the portal's own origin.
 */
export const sourceFileFrameUrl = (sourceId: string, page?: number): string =>
  `/api/admin/kb/sources/${encodeURIComponent(sourceId)}/file${page ? `#page=${String(page)}` : ''}`;

export const del = <T>(path: string) => api<T>('DELETE', path);
export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
